import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import JourneyInsights from './JourneyInsights';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../runtimeConfig';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useAuth } from '../context/AuthContext';
import dayjs from 'dayjs';
import _ from 'lodash';

// AG Grid Components
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry, AllCommunityModule, themeQuartz } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

// Material-UI Components
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import { Tabs, Tab } from '@mui/material';

// Recharts Components
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

// Recharts default tick fill is #666 — too dark on dark card backgrounds.
const AXIS_TICK = { fill: '#ffffff', fontSize: 12 };

const SurveyDashboard = () => {
  const navigate = useNavigate();
  const { userAttributes } = useAuth();
  const journeyGridRef = useRef(null);

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Loading and data state
  const [loading, setLoading] = useState(false);
  const [rawData, setRawData] = useState([]);
  const [journeyTableData, setJourneyTableData] = useState([]);
  const [journeyTableLoading, setJourneyTableLoading] = useState(false);
  const [journeyColumnDefs, setJourneyColumnDefs] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Filter options state
  const [availableProjects, setAvailableProjects] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    brand_names: [],
    product_categories: [],
    moment_ids: [],
  });

  // Filter state
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('All');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedMoment, setSelectedMoment] = useState('All');
  const [selectedStage, setSelectedStage] = useState('completed');
  const [summaryProjectInfo, setSummaryProjectInfo] = useState(null);

  // Journey Table specific filter state
  const [journeySelectedProject, setJourneySelectedProject] = useState('');
  const [journeySelectedBrand, setJourneySelectedBrand] = useState('All');
  const [journeySelectedCategory, setJourneySelectedCategory] = useState('All');
  const [journeySelectedMoment, setJourneySelectedMoment] = useState('All');
  const [journeySelectedStage, setJourneySelectedStage] = useState('All');
  const [journeyCompletionProjectInfo, setJourneyCompletionProjectInfo] = useState(null);
  const [journeyFilterOptions, setJourneyFilterOptions] = useState({
    brand_names: [],
    product_categories: [],
    completed_block_names: [],
    moment_completion_statuses: [],
  });

  // Breakdown modal state
  const [breakdownModalOpen, setBreakdownModalOpen] = useState(false);
  const [breakdownModalData, setBreakdownModalData] = useState(null);

  // Fetch available projects on mount
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        console.log('🔄 Fetching projects...');
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();
        
        if (!token) {
          console.warn('⚠️  No token available');
          return;
        }

        console.log('📡 API URL:', API_URL);
        const response = await axios.get(
          `${API_URL}/api/analytics-views/projects`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        console.log('✅ Projects response:', response.data);

        if (response.data.success) {
          // Server already enforces access control via Cognito groups — use as-is
          const projects = response.data.projects;
          console.log('📊 Projects from server:', projects);
          setAvailableProjects(projects);
        }
      } catch (error) {
        console.error('❌ Failed to fetch projects:', error);
        console.error('   Status:', error.response?.status);
        console.error('   Data:', error.response?.data);
        
        // Fallback to Cognito groups if API fails
        if (userAttributes?.projectAccess) {
          console.log('⚠️  Using fallback - projectAccess from Cognito:', userAttributes.projectAccess);
          setAvailableProjects(userAttributes.projectAccess);
        } else if (userAttributes?.role === 'admin') {
          console.warn('⚠️  Admin user but no fallback available');
        }
      }
    };

    if (userAttributes) {
      fetchProjects();
    }
  }, [userAttributes]);

  // Fetch filter options and project info when summary project changes
  useEffect(() => {
    if (selectedProject) {
      fetchFilterOptions();
      fetchSummaryProjectInfo();
    } else {
      setSummaryProjectInfo(null);
    }
  }, [selectedProject]);

  // Fetch journey filter options and project info when journey project changes
  useEffect(() => {
    if (journeySelectedProject) {
      fetchJourneyFilterOptions();
      fetchJourneyProjectInfo();
    } else {
      setJourneyCompletionProjectInfo(null);
    }
  }, [journeySelectedProject]);

  // Generate column definitions for Journey table
  const generateJourneyColumns = useCallback(() => {
    const columnMapping = [
      { field: 'session_id', headerName: 'Session ID', width: 150 },
      { field: 'responder_id', headerName: 'Responder ID', width: 150 },
      { field: 'project_code', headerName: 'Project Code', width: 130 },
      { field: 'product_category', headerName: 'Category', width: 150 },
      { field: 'brand_name', headerName: 'Brand', width: 150 },
      { field: 'session_created_at', headerName: 'Session Created', width: 180 },
      {
        field: 'moment_completion_status',
        headerName: 'Status',
        width: 130,
        cellRenderer: (params) => {
          const status = params.value;
          let bgColor = 'transparent';
          let textColor = '#374151';

          if (status === 'completed') {
            bgColor = 'transparent';
            textColor = '#059669';
          } else if (status === 'in_progress') {
            bgColor = 'transparent';
            textColor = '#d97706';
          } else if (status === 'not_started') {
            bgColor = 'transparent';
            textColor = '#dc2626';
          }

          return (
            <div style={{
              backgroundColor: bgColor,
              color: textColor,
              padding: '4px 0',
              fontWeight: '500',
              textAlign: 'left',
            }}>
              {status}
            </div>
          );
        },
      },
      { field: 'occasion_numbers', headerName: 'Occasions', width: 120 },
      { field: 'completed_block_name', headerName: 'Completed Block', width: 180 },
    ];

    return columnMapping;
  }, []);

  // Generate journey table columns when data loads
  useEffect(() => {
    if (journeyTableData && journeyTableData.length > 0) {
      const generatedColumns = generateJourneyColumns();
      setJourneyColumnDefs(generatedColumns);
      console.log('✅ Generated journey table columns');
    }
  }, [journeyTableData, generateJourneyColumns]);

  const fetchFilterOptions = async () => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      if (!token) return;

      const response = await axios.get(
        `${API_URL}/api/analytics-views/summary-filter-options/${selectedProject}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setFilterOptions(response.data.filter_options);
      }
    } catch (error) {
      console.error('Failed to fetch filter options:', error);
    }
  };

  const fetchJourneyFilterOptions = async () => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      if (!token) return;

      const response = await axios.get(
        `${API_URL}/api/analytics-views/journey-filter-options/${journeySelectedProject}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setJourneyFilterOptions(response.data.filter_options);
      }
    } catch (error) {
      console.error('Failed to fetch journey filter options:', error);
    }
  };

  const fetchSummaryProjectInfo = async () => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      if (!token) return;
      const response = await axios.get(
        `${API_URL}/api/analytics-views/project-info/${selectedProject}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) setSummaryProjectInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch summary project info:', error);
    }
  };

  const fetchJourneyProjectInfo = async () => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      if (!token) return;
      const response = await axios.get(
        `${API_URL}/api/analytics-views/project-info/${journeySelectedProject}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) setJourneyCompletionProjectInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch journey project info:', error);
    }
  };

  // Fetch dashboard data
  const fetchDashboardData = async () => {
    if (!selectedProject) return;

    setLoading(true);
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      if (!token) {
        alert('Authentication required. Please log in again.');
        navigate('/login');
        return;
      }

      const params = new URLSearchParams();
      if (selectedBrand !== 'All') params.append('brand_name', selectedBrand);
      if (selectedCategory !== 'All') params.append('product_category', selectedCategory);
      if (selectedMoment !== 'All') params.append('moment_id', selectedMoment);

      const response = await axios.get(
        `${API_URL}/api/analytics-views/summary/${selectedProject}?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setRawData(response.data.data);
        if (response.data.meta?.project_info) setSummaryProjectInfo(response.data.meta.project_info);
        setLastUpdated(new Date());
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please log in again.');
        navigate('/login');
      } else {
        alert('Failed to load data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch journey completion table data
  const fetchJourneyTableData = async () => {
    if (!journeySelectedProject) return;

    setJourneyTableLoading(true);
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      if (!token) {
        alert('Authentication required. Please log in again.');
        navigate('/login');
        return;
      }

      const params = new URLSearchParams();
      if (journeySelectedBrand !== 'All') params.append('brand_name', journeySelectedBrand);
      if (journeySelectedCategory !== 'All') params.append('product_category', journeySelectedCategory);
      if (journeySelectedMoment !== 'All') params.append('completed_block_name', journeySelectedMoment);
      if (journeySelectedStage !== 'All') params.append('moment_completion_status', journeySelectedStage);

      const response = await axios.get(
        `${API_URL}/api/analytics-views/journey-completion-table/${journeySelectedProject}?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setJourneyTableData(response.data.data);
        if (response.data.meta?.project_info) setJourneyCompletionProjectInfo(response.data.meta.project_info);
        setLastUpdated(new Date());
      }
    } catch (error) {
      console.error('Failed to fetch journey table data:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please log in again.');
        navigate('/login');
      } else {
        alert('Failed to load journey data. Please try again.');
      }
    } finally {
      setJourneyTableLoading(false);
    }
  };

  // Refresh data handler
  const handleRefresh = () => {
    if (rawData.length > 0) {
      fetchDashboardData();
    }
  };

  // Get unique stages from data for dropdown
  const uniqueStages = useMemo(() => {
    if (!rawData.length) return [];
    const stages = [...new Set(rawData.map(row => row.moment_stage).filter(Boolean))];
    return stages.sort();
  }, [rawData]);

  // Compute daily chart data
  const dailyChartData = useMemo(() => {
    if (!rawData.length) return [];

    // Filter by selected stage
    const filteredData = selectedStage === 'All'
      ? rawData
      : rawData.filter(row => row.moment_stage === selectedStage);

    // Group by date
    const groupedByDate = _.groupBy(filteredData, row =>
      dayjs(row.created_at).format('YYYY-MM-DD')
    );

    // Calculate unique responders per day
    const chartData = Object.entries(groupedByDate)
      .map(([date, rows]) => ({
        date: date,
        uniqueResponders: new Set(rows.map(r => r.responder_id)).size,
        totalInteractions: rows.length,
        totalResponses: rows.length,
      }))
      .sort((a, b) => a.date.localeCompare(b.date));

    return chartData;
  }, [rawData, selectedStage]);

  // Compute cumulative chart data (CORRECTED: Total responses)
  const cumulativeChartData = useMemo(() => {
    if (!dailyChartData.length) return [];

    let cumulative = 0;
    return dailyChartData.map(day => {
      cumulative += day.totalResponses; // Changed from uniqueResponders
      return {
        date: day.date,
        cumulative: cumulative,
      };
    });
  }, [dailyChartData]);

  // Compute moment chart data (NEW - Chart 3)
  const momentChartData = useMemo(() => {
    if (!rawData.length) return [];

    // Filter by selected stage
    const filteredData = selectedStage === 'All'
      ? rawData
      : rawData.filter(row => row.moment_stage === selectedStage);

    // Group by moment_id and count total responses
    const momentData = _(filteredData)
      .groupBy('moment_id')
      .map((rows, moment) => ({
        moment_id: moment || 'Unknown',
        count: rows.length, // Total responses
      }))
      .orderBy('count', 'desc') // Sort by count descending
      .value();

    return momentData;
  }, [rawData, selectedStage]);

  // Compute occasion chart data (NEW - Chart 4)
  const occasionChartData = useMemo(() => {
    if (!rawData.length) return [];

    // Filter by selected stage
    const filteredData = selectedStage === 'All'
      ? rawData
      : rawData.filter(row => row.moment_stage === selectedStage);

    // Group by occasion_number and count total responses
    const occasionData = _(filteredData)
      .groupBy('occasion_number')
      .map((rows, occasion) => ({
        occasion_number: Number(occasion),
        occasion_label: `occasion_${occasion}`, // Format as "occasion_0", "occasion_1"
        count: rows.length, // Total responses
      }))
      .orderBy('occasion_number', 'asc') // Sort numerically ascending
      .value();

    return occasionData;
  }, [rawData, selectedStage]);

  // Compute daily unique responders data (NEW - Chart 5)
  const dailyUniqueRespondersData = useMemo(() => {
    if (!rawData.length) return [];

    // Filter by selected stage
    const filteredData = selectedStage === 'All'
      ? rawData
      : rawData.filter(row => row.moment_stage === selectedStage);

    // Group by date
    const groupedByDate = _.groupBy(filteredData, row =>
      dayjs(row.created_at).format('YYYY-MM-DD')
    );

    // Calculate unique responders per day
    const chartData = Object.entries(groupedByDate)
      .map(([date, rows]) => ({
        date: date,
        uniqueResponders: new Set(rows.map(r => r.responder_id)).size,
      }))
      .sort((a, b) => a.date.localeCompare(b.date));

    return chartData;
  }, [rawData, selectedStage]);

  // Compute cumulative unique responders data (NEW - Chart 6)
  const cumulativeUniqueRespondersData = useMemo(() => {
    if (!rawData.length) return [];

    // Filter by selected stage
    const filteredData = selectedStage === 'All'
      ? rawData
      : rawData.filter(row => row.moment_stage === selectedStage);

    // Sort by date and track unique responders cumulatively
    const sortedData = _.sortBy(filteredData, row => row.created_at);
    const uniqueRespondersSet = new Set();
    const dailySnapshots = new Map();

    sortedData.forEach(row => {
      const date = dayjs(row.created_at).format('YYYY-MM-DD');
      uniqueRespondersSet.add(row.responder_id);
      dailySnapshots.set(date, uniqueRespondersSet.size);
    });

    // Convert to array format
    const chartData = Array.from(dailySnapshots.entries())
      .map(([date, cumulative]) => ({
        date: date,
        cumulative: cumulative,
      }))
      .sort((a, b) => a.date.localeCompare(b.date));

    return chartData;
  }, [rawData, selectedStage]);

  // Total responses count
  const totalResponses = useMemo(() => {
    if (!rawData.length) return 0;
    const filteredData = selectedStage === 'All'
      ? rawData
      : rawData.filter(row => row.moment_stage === selectedStage);
    return filteredData.length;
  }, [rawData, selectedStage]);

  // Handle bar click - Chart 1 (Daily)
  const handleDailyBarClick = useCallback((data) => {
    if (!data || !data.date) return;

    const clickedDate = data.date;
    const dayData = rawData.filter(row => {
      const rowDate = dayjs(row.created_at).format('YYYY-MM-DD');
      const matchesDate = rowDate === clickedDate;
      const matchesStage = selectedStage === 'All' || row.moment_stage === selectedStage;
      return matchesDate && matchesStage;
    });

    // Show all responses (not just unique responders)
    const responses = dayData.map(row => ({
      responder_id: row.responder_id,
      project_code: row.project_code,
      brand_name: row.brand_name,
      product_category: row.product_category,
      moment_stage: row.moment_stage,
      created_at: row.created_at,
    }));

    setBreakdownModalData({
      date: clickedDate,
      count: responses.length,
      responders: responses,
    });
    setBreakdownModalOpen(true);
  }, [rawData, selectedStage]);

  // Handle bar click - Chart 3 (Moment)
  const handleMomentBarClick = useCallback((data) => {
    if (!data || !data.moment_id) return;

    const clickedMoment = data.moment_id;
    const momentData = rawData.filter(row => {
      const matchesMoment = row.moment_id === clickedMoment;
      const matchesStage = selectedStage === 'All' || row.moment_stage === selectedStage;
      return matchesMoment && matchesStage;
    });

    // Show all responses (not just unique responders)
    const responses = momentData.map(row => ({
      responder_id: row.responder_id,
      project_code: row.project_code,
      brand_name: row.brand_name,
      product_category: row.product_category,
      moment_stage: row.moment_stage,
      created_at: row.created_at,
    }));

    setBreakdownModalData({
      date: `Moment: ${clickedMoment}`,
      count: responses.length,
      responders: responses,
    });
    setBreakdownModalOpen(true);
  }, [rawData, selectedStage]);

  // Handle bar click - Chart 4 (Occasion)
  const handleOccasionBarClick = useCallback((data) => {
    if (!data || data.occasion_number === undefined) return;

    const clickedOccasion = data.occasion_number;
    const occasionData = rawData.filter(row => {
      const matchesOccasion = Number(row.occasion_number) === clickedOccasion;
      const matchesStage = selectedStage === 'All' || row.moment_stage === selectedStage;
      return matchesOccasion && matchesStage;
    });

    // Show all responses (not just unique responders)
    const responses = occasionData.map(row => ({
      responder_id: row.responder_id,
      project_code: row.project_code,
      brand_name: row.brand_name,
      product_category: row.product_category,
      moment_stage: row.moment_stage,
      created_at: row.created_at,
    }));

    setBreakdownModalData({
      date: `Occasion: ${data.occasion_label}`,
      count: responses.length,
      responders: responses,
    });
    setBreakdownModalOpen(true);
  }, [rawData, selectedStage]);

  // Handle bar click - Chart 5 (Daily Unique Responders)
  const handleDailyUniqueRespondersBarClick = useCallback((data) => {
    if (!data || !data.date) return;

    const clickedDate = data.date;
    const dayData = rawData.filter(row => {
      const rowDate = dayjs(row.created_at).format('YYYY-MM-DD');
      const matchesDate = rowDate === clickedDate;
      const matchesStage = selectedStage === 'All' || row.moment_stage === selectedStage;
      return matchesDate && matchesStage;
    });

    // Get unique responders for this date
    const uniqueRespondersMap = {};
    dayData.forEach(row => {
      if (!uniqueRespondersMap[row.responder_id]) {
        uniqueRespondersMap[row.responder_id] = row;
      }
    });

    const uniqueResponders = Object.values(uniqueRespondersMap);

    setBreakdownModalData({
      date: clickedDate,
      count: uniqueResponders.length,
      responders: uniqueResponders.map(row => ({
        responder_id: row.responder_id,
        project_code: row.project_code,
        brand_name: row.brand_name,
        product_category: row.product_category,
        moment_stage: row.moment_stage,
        created_at: row.created_at,
      })),
    });
    setBreakdownModalOpen(true);
  }, [rawData, selectedStage]);

  // Export daily chart CSV
  const exportDailyCSV = () => {
    if (!dailyChartData.length) return;

    const csv = [
      ['Date', 'Total Responses'].join(','),
      ...dailyChartData.map(row => [row.date, row.totalResponses].join(','))
    ].join('\n');

    downloadCSV(csv, `daily-responses-${selectedProject}-${new Date().toISOString().split('T')[0]}.csv`);
  };

  // Export cumulative chart CSV
  const exportCumulativeCSV = () => {
    if (!cumulativeChartData.length) return;

    const csv = [
      ['Date', 'Cumulative Responses'].join(','),
      ...cumulativeChartData.map(row => [row.date, row.cumulative].join(','))
    ].join('\n');

    downloadCSV(csv, `cumulative-responses-${selectedProject}-${new Date().toISOString().split('T')[0]}.csv`);
  };

  // Export moment chart CSV (NEW)
  const exportMomentCSV = () => {
    if (!momentChartData.length) return;

    const csv = [
      ['Moment ID', 'Total Responses'].join(','),
      ...momentChartData.map(row => [row.moment_id, row.count].join(','))
    ].join('\n');

    downloadCSV(csv, `moment-responses-${selectedProject}-${new Date().toISOString().split('T')[0]}.csv`);
  };

  // Export occasion chart CSV (NEW)
  const exportOccasionCSV = () => {
    if (!occasionChartData.length) return;

    const csv = [
      ['Occasion Number', 'Occasion Label', 'Total Responses'].join(','),
      ...occasionChartData.map(row => [row.occasion_number, row.occasion_label, row.count].join(','))
    ].join('\n');

    downloadCSV(csv, `occasion-responses-${selectedProject}-${new Date().toISOString().split('T')[0]}.csv`);
  };

  // Export daily unique responders CSV (NEW)
  const exportDailyUniqueRespondersCSV = () => {
    if (!dailyUniqueRespondersData.length) return;

    const csv = [
      ['Date', 'Unique Responders'].join(','),
      ...dailyUniqueRespondersData.map(row => [row.date, row.uniqueResponders].join(','))
    ].join('\n');

    downloadCSV(csv, `daily-unique-responders-${selectedProject}-${new Date().toISOString().split('T')[0]}.csv`);
  };

  // Export cumulative unique responders CSV (NEW)
  const exportCumulativeUniqueRespondersCSV = () => {
    if (!cumulativeUniqueRespondersData.length) return;

    const csv = [
      ['Date', 'Cumulative Unique Responders'].join(','),
      ...cumulativeUniqueRespondersData.map(row => [row.date, row.cumulative].join(','))
    ].join('\n');

    downloadCSV(csv, `cumulative-unique-responders-${selectedProject}-${new Date().toISOString().split('T')[0]}.csv`);
  };

  // Export breakdown modal data
  const exportBreakdownCSV = () => {
    if (!breakdownModalData?.responders.length) return;

    const csv = [
      ['Responder ID', 'Project Code', 'Brand', 'Category', 'Stage', 'Timestamp'].join(','),
      ...breakdownModalData.responders.map(r =>
        [r.responder_id, r.project_code, r.brand_name, r.product_category, r.moment_stage, r.created_at].join(',')
      ),
    ].join('\n');

    downloadCSV(csv, `breakdown-${breakdownModalData.date}-${new Date().toISOString().split('T')[0]}.csv`);
  };

  // Utility function to download CSV
  const downloadCSV = (content, filename) => {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  // Load saved column state from localStorage (Journey table)
  useEffect(() => {
    if (userAttributes?.username && journeyTableData.length > 0) {
      const savedState = localStorage.getItem(`journey-table-layout-${userAttributes.username}`);
      if (savedState && journeyGridRef.current) {
        try {
          const state = JSON.parse(savedState);
          journeyGridRef.current.api.applyColumnState({ state, applyOrder: true });
        } catch (error) {
          console.error('Failed to restore journey table column layout:', error);
        }
      }
    }
  }, [userAttributes, journeyTableData]);

  // Save column state on column moved (Journey table)
  const onJourneyColumnMoved = useCallback(() => {
    if (journeyGridRef.current && userAttributes?.username) {
      const columnState = journeyGridRef.current.api.getColumnState();
      localStorage.setItem(
        `journey-table-layout-${userAttributes.username}`,
        JSON.stringify(columnState)
      );
    }
  }, [userAttributes]);

  // Save column state when visibility changes (Journey table)
  const onJourneyColumnVisible = useCallback(() => {
    if (journeyGridRef.current && userAttributes?.username) {
      const columnState = journeyGridRef.current.api.getColumnState();
      localStorage.setItem(
        `journey-table-layout-${userAttributes.username}`,
        JSON.stringify(columnState)
      );
    }
  }, [userAttributes]);

  // Reset journey table layout
  const resetJourneyLayout = () => {
    if (journeyGridRef.current && userAttributes?.username) {
      journeyGridRef.current.api.resetColumnState();
      localStorage.removeItem(`journey-table-layout-${userAttributes.username}`);
    }
  };

  // Clear all journey filters
  const clearJourneyFilters = () => {
    if (journeyGridRef.current) {
      journeyGridRef.current.api.setFilterModel(null);
    }
  };

  // Export journey table data to CSV
  const exportJourneyTableCSV = () => {
    if (!journeyTableData.length) return;

    const headers = [
      'Session ID', 'Responder ID', 'Project Code',
      'Category', 'Brand', 'Session Created',
      'Status', 'Occasions', 'Completed Block'
    ];

    const rows = journeyTableData.map(row => [
      row.session_id,
      row.responder_id,
      row.project_code,
      row.product_category,
      row.brand_name,
      row.session_created_at,
      row.moment_completion_status,
      row.occasion_numbers,
      row.completed_block_name,
    ]);

    const csv = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell || ''}"`).join(',')),
    ].join('\n');

    downloadCSV(csv, `journey-completion-table-${new Date().toISOString().split('T')[0]}.csv`);
  };

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <TooltipContainer>
          <TooltipLabel>{label}</TooltipLabel>
          {payload.map((entry, index) => (
            <TooltipValue key={index} color={entry.color}>
              {entry.name}: {entry.value}
            </TooltipValue>
          ))}
        </TooltipContainer>
      );
    }
    return null;
  };

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/')}>
          <ArrowBackIcon /> Back to Home
        </BackButton>
        <Title>Survey Dashboard</Title>
      </Header>

      <Content>
        {/* Tabs */}
        <TabsContainer>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{
              '& .MuiTab-root': { color: '#6b7280', fontWeight: 600 },
              '& .Mui-selected': { color: '#1976d2' },
            }}
          >
            <Tab label="Summary Survey Response" />
            <Tab label="Journey Completion Table" />
            <Tab label="Journey Completion Insights" />
          </Tabs>
        </TabsContainer>

        {/* Tab 1: Summary Survey Response */}
        {activeTab === 0 && (
          <>
            {/* Control Panel */}
            <ControlPanel>
              <FilterSection>
                <FilterLabel>Project Code:</FilterLabel>
                <FilterSelect
                  value={selectedProject}
                  onChange={(e) => setSelectedProject(e.target.value)}
                >
                  <option value="">Select Project</option>
                  {availableProjects.map(project => (
                    <option key={project} value={project}>{project}</option>
                  ))}
                </FilterSelect>

                <FilterLabel>Brand:</FilterLabel>
                <FilterSelect
                  value={selectedBrand}
                  onChange={(e) => setSelectedBrand(e.target.value)}
                  disabled={!selectedProject}
                >
                  <option value="All">All</option>
                  {filterOptions.brand_names.map(brand => (
                    <option key={brand} value={brand}>{brand}</option>
                  ))}
                </FilterSelect>

                <FilterLabel>Category:</FilterLabel>
                <FilterSelect
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  disabled={!selectedProject}
                >
                  <option value="All">All</option>
                  {filterOptions.product_categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </FilterSelect>

                <FilterLabel>Moment:</FilterLabel>
                <FilterSelect
                  value={selectedMoment}
                  onChange={(e) => setSelectedMoment(e.target.value)}
                  disabled={!selectedProject}
                >
                  <option value="All">All</option>
                  {filterOptions.moment_ids.map(moment => (
                    <option key={moment} value={moment}>{moment}</option>
                  ))}
                </FilterSelect>

                <FilterLabel>Stage:</FilterLabel>
                <FilterSelect
                  value={selectedStage}
                  onChange={(e) => setSelectedStage(e.target.value)}
                  disabled={!rawData.length}
                >
                  <option value="All">All</option>
                  <option value="completed">Completed</option>
                  {uniqueStages.map(stage => (
                    <option key={stage} value={stage}>{stage}</option>
                  ))}
                </FilterSelect>

                {summaryProjectInfo && (
                  <ProjectDateBadge>
                    <StatusDot active={summaryProjectInfo.status === 'active'} title={summaryProjectInfo.status} />
                    <strong>{summaryProjectInfo.project_name}</strong>
                    <span>&nbsp;|&nbsp;Study Period:&nbsp;</span>
                    <strong>{String(summaryProjectInfo.start_date).substring(0, 10)} 00:00:00</strong>
                    <span>&nbsp;→&nbsp;</span>
                    <strong>{summaryProjectInfo.end_date ? `${String(summaryProjectInfo.end_date).substring(0, 10)} 23:00:00` : 'Open'}</strong>
                  </ProjectDateBadge>
                )}

                <ActionButton onClick={fetchDashboardData} disabled={!selectedProject || loading}>
                  {loading ? 'Loading...' : 'Load Data'}
                </ActionButton>
              </FilterSection>

              <ExportSection>
                <ActionButton onClick={handleRefresh} disabled={!rawData.length || loading}>
                  <RefreshIcon /> {loading ? 'Refreshing...' : 'Refresh'}
                </ActionButton>
                <ActionButton onClick={exportDailyCSV} disabled={!dailyChartData.length}>
                  <DownloadIcon /> Export Daily
                </ActionButton>
                <ActionButton onClick={exportCumulativeCSV} disabled={!cumulativeChartData.length}>
                  <DownloadIcon /> Export Cumulative
                </ActionButton>
                <ActionButton onClick={exportMomentCSV} disabled={!momentChartData.length}>
                  <DownloadIcon /> Export Moment
                </ActionButton>
                <ActionButton onClick={exportOccasionCSV} disabled={!occasionChartData.length}>
                  <DownloadIcon /> Export Occasion
                </ActionButton>
                <ActionButton onClick={exportDailyUniqueRespondersCSV} disabled={!dailyUniqueRespondersData.length}>
                  <DownloadIcon /> Export Daily Unique
                </ActionButton>
                <ActionButton onClick={exportCumulativeUniqueRespondersCSV} disabled={!cumulativeUniqueRespondersData.length}>
                  <DownloadIcon /> Export Cumulative Unique
                </ActionButton>
              </ExportSection>
            </ControlPanel>

            {/* Status Bar */}
            {lastUpdated && (
              <StatusBar>
                <StatusText>
                  Last refreshed: {lastUpdated.toLocaleString()} |
                  Showing: {dailyChartData.length} days |
                  Total responses: {totalResponses}
                </StatusText>
              </StatusBar>
            )}

            {/* Charts */}
            {rawData.length > 0 ? (
              <ChartsContainer>
                {/* Row 1: Daily & Cumulative Charts */}
                <ChartRow>
                  {/* Chart 1: Daily Responses */}
                  <ChartCard>
                    <ChartHeader>
                      <ChartTitle>Daily Survey Responses</ChartTitle>
                      <ChartSubtitle>Click bars to see response breakdown</ChartSubtitle>
                    </ChartHeader>
                    <ResponsiveContainer width="100%" height={450}>
                      <BarChart
                        data={dailyChartData}
                        margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                      >
                        <XAxis
                          dataKey="date"
                          angle={-45}
                          textAnchor="end"
                          height={80}
                          tick={{ ...AXIS_TICK, fontSize: 12 }}
                        />
                        <YAxis tick={AXIS_TICK} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Bar
                          dataKey="totalResponses"
                          fill="#14b8a6"
                          name="Total Responses"
                          onClick={handleDailyBarClick}
                          cursor="pointer"
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>

                  {/* Chart 2: Cumulative Responses */}
                  <ChartCard>
                    <ChartHeader>
                      <ChartTitle>Cumulative Survey Responses</ChartTitle>
                      <ChartSubtitle>Growth over selected period</ChartSubtitle>
                    </ChartHeader>
                    <ResponsiveContainer width="100%" height={450}>
                      <AreaChart
                        data={cumulativeChartData}
                        margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                      >
                        <XAxis
                          dataKey="date"
                          angle={-45}
                          textAnchor="end"
                          height={80}
                          tick={{ ...AXIS_TICK, fontSize: 12 }}
                        />
                        <YAxis tick={AXIS_TICK} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Area
                          type="monotone"
                          dataKey="cumulative"
                          fill="#14b8a6"
                          stroke="#0d9488"
                          name="Cumulative Responses"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </ChartRow>

                {/* Row 2: Moment & Occasion Charts (NEW) */}
                <ChartRow>
                  {/* Chart 3: Responses by Moment */}
                  <ChartCard>
                    <ChartHeader>
                      <ChartTitle>Responses by Moment Stage</ChartTitle>
                      <ChartSubtitle>Click bars to see response breakdown</ChartSubtitle>
                    </ChartHeader>
                    <ResponsiveContainer width="100%" height={450}>
                      <BarChart
                        data={momentChartData}
                        layout="vertical"
                        margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                      >
                        <XAxis type="number" tick={AXIS_TICK} />
                        <YAxis
                          type="category"
                          dataKey="moment_id"
                          width={150}
                          tick={AXIS_TICK}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Bar
                          dataKey="count"
                          fill="#14b8a6"
                          name="Total Responses"
                          onClick={handleMomentBarClick}
                          cursor="pointer"
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>

                  {/* Chart 4: Responses by Occasion */}
                  <ChartCard>
                    <ChartHeader>
                      <ChartTitle>Responses by Occasion Number</ChartTitle>
                      <ChartSubtitle>Click bars to see response breakdown</ChartSubtitle>
                    </ChartHeader>
                    <ResponsiveContainer width="100%" height={450}>
                      <BarChart
                        data={occasionChartData}
                        layout="vertical"
                        margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                      >
                        <XAxis type="number" tick={AXIS_TICK} />
                        <YAxis
                          type="category"
                          dataKey="occasion_label"
                          width={120}
                          tick={AXIS_TICK}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Bar
                          dataKey="count"
                          fill="#14b8a6"
                          name="Total Responses"
                          onClick={handleOccasionBarClick}
                          cursor="pointer"
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </ChartRow>

                {/* Row 3: Daily & Cumulative Unique Responders (NEW) */}
                <ChartRow>
                  {/* Chart 5: Daily Unique Responders */}
                  <ChartCard>
                    <ChartHeader>
                      <ChartTitle>Daily Unique Responders</ChartTitle>
                      <ChartSubtitle>Click bars to see unique responder breakdown</ChartSubtitle>
                    </ChartHeader>
                    <ResponsiveContainer width="100%" height={450}>
                      <BarChart
                        data={dailyUniqueRespondersData}
                        margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                      >
                        <XAxis
                          dataKey="date"
                          angle={-45}
                          textAnchor="end"
                          height={80}
                          tick={{ ...AXIS_TICK, fontSize: 12 }}
                        />
                        <YAxis tick={AXIS_TICK} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Bar
                          dataKey="uniqueResponders"
                          fill="#63dcf1ff"
                          name="Unique Responders"
                          onClick={handleDailyUniqueRespondersBarClick}
                          cursor="pointer"
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartCard>

                  {/* Chart 6: Cumulative Unique Responders */}
                  <ChartCard>
                    <ChartHeader>
                      <ChartTitle>Cumulative Unique Responders</ChartTitle>
                      <ChartSubtitle>Growth of unique responders over time</ChartSubtitle>
                    </ChartHeader>
                    <ResponsiveContainer width="100%" height={450}>
                      <AreaChart
                        data={cumulativeUniqueRespondersData}
                        margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                      >
                        <XAxis
                          dataKey="date"
                          angle={-45}
                          textAnchor="end"
                          height={80}
                          tick={{ ...AXIS_TICK, fontSize: 12 }}
                        />
                        <YAxis tick={AXIS_TICK} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Area
                          type="monotone"
                          dataKey="cumulative"
                          fill="#63dcf1ff"
                          stroke="#63dcf1ff"
                          name="Cumulative Unique Responders"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </ChartRow>
              </ChartsContainer>
            ) : (
              <EmptyState>
                <EmptyIcon>📊</EmptyIcon>
                <EmptyText>
                  {loading
                    ? 'Loading chart data...'
                    : 'Select filters and click "Load Data" to view charts'}
                </EmptyText>
              </EmptyState>
            )}
          </>
        )}

        {/* Tab 2: Journey Completion Table */}
        {activeTab === 1 && (
          <>
            {/* Control Panel */}
            <ControlPanel>
              <FilterSection>
                <FilterLabel>Project Code:</FilterLabel>
                <FilterSelect
                  value={journeySelectedProject}
                  onChange={(e) => setJourneySelectedProject(e.target.value)}
                >
                  <option value="">Select Project</option>
                  {availableProjects.map(project => (
                    <option key={project} value={project}>{project}</option>
                  ))}
                </FilterSelect>

                <FilterLabel>Brand:</FilterLabel>
                <FilterSelect
                  value={journeySelectedBrand}
                  onChange={(e) => setJourneySelectedBrand(e.target.value)}
                  disabled={!journeySelectedProject}
                >
                  <option value="All">All</option>
                  {journeyFilterOptions.brand_names.map(brand => (
                    <option key={brand} value={brand}>{brand}</option>
                  ))}
                </FilterSelect>

                <FilterLabel>Category:</FilterLabel>
                <FilterSelect
                  value={journeySelectedCategory}
                  onChange={(e) => setJourneySelectedCategory(e.target.value)}
                  disabled={!journeySelectedProject}
                >
                  <option value="All">All</option>
                  {journeyFilterOptions.product_categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </FilterSelect>

                <FilterLabel>Moment:</FilterLabel>
                <FilterSelect
                  value={journeySelectedMoment}
                  onChange={(e) => setJourneySelectedMoment(e.target.value)}
                  disabled={!journeySelectedProject}
                >
                  <option value="All">All</option>
                  {journeyFilterOptions.completed_block_names.map(moment => (
                    <option key={moment} value={moment}>{moment}</option>
                  ))}
                </FilterSelect>

                <FilterLabel>Stage:</FilterLabel>
                <FilterSelect
                  value={journeySelectedStage}
                  onChange={(e) => setJourneySelectedStage(e.target.value)}
                  disabled={!journeySelectedProject}
                >
                  <option value="All">All</option>
                  {journeyFilterOptions.moment_completion_statuses.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </FilterSelect>

                {journeyCompletionProjectInfo && (
                  <ProjectDateBadge>
                    <StatusDot active={journeyCompletionProjectInfo.status === 'active'} title={journeyCompletionProjectInfo.status} />
                    <strong>{journeyCompletionProjectInfo.project_name}</strong>
                    <span>&nbsp;|&nbsp;Study Period:&nbsp;</span>
                    <strong>{String(journeyCompletionProjectInfo.start_date).substring(0, 10)} 00:00:00</strong>
                    <span>&nbsp;→&nbsp;</span>
                    <strong>{journeyCompletionProjectInfo.end_date ? `${String(journeyCompletionProjectInfo.end_date).substring(0, 10)} 23:00:00` : 'Open'}</strong>
                  </ProjectDateBadge>
                )}

                <ActionButton onClick={fetchJourneyTableData} disabled={!journeySelectedProject || journeyTableLoading}>
                  {journeyTableLoading ? 'Loading...' : 'Load Data'}
                </ActionButton>
              </FilterSection>

              <ExportSection>
                <ActionButton onClick={() => clearJourneyFilters()} disabled={journeyTableData.length === 0} title="Clear all column filters">
                  Clear Filters
                </ActionButton>
                <ActionButton onClick={exportJourneyTableCSV} disabled={journeyTableData.length === 0}>
                  <DownloadIcon /> Export Data
                </ActionButton>
                <ActionButton onClick={resetJourneyLayout} $variant="secondary" title="Reset column order and visibility">
                  Reset Layout
                </ActionButton>
              </ExportSection>
            </ControlPanel>

            {/* Status Bar */}
            {lastUpdated && journeyTableData.length > 0 && (
              <StatusBar>
                <StatusText>
                  Last refreshed: {lastUpdated.toLocaleString()} | Showing: {journeyTableData.length} records
                </StatusText>
              </StatusBar>
            )}

            {/* AG Grid Table */}
            {journeyTableData.length > 0 ? (
              <JourneyGridContainer className="ag-theme-quartz">
                <AgGridReact
                  ref={journeyGridRef}
                  rowData={journeyTableData}
                  columnDefs={journeyColumnDefs}
                  theme={themeQuartz}
                  defaultColDef={{
                    sortable: true,
                    filter: true,
                    resizable: true,
                    floatingFilter: true,
                  }}
                  pagination={true}
                  paginationPageSize={50}
                  rowSelection={{
                    mode: 'multiRow',
                    checkboxes: true,
                    headerCheckbox: true,
                  }}
                  onColumnMoved={onJourneyColumnMoved}
                  onColumnVisible={onJourneyColumnVisible}
                  animateRows={true}
                />
              </JourneyGridContainer>
            ) : (
              <EmptyState>
                <EmptyIcon>📋</EmptyIcon>
                <EmptyText>
                  {journeyTableLoading
                    ? 'Loading journey data...'
                    : 'Select filters and click "Load Data" to view journey completion records'}
                </EmptyText>
              </EmptyState>
            )}
          </>
        )}

        {/* Tab 3: Journey Completion Insights */}
        {activeTab === 2 && (
          <JourneyInsights
            userAttributes={userAttributes}
          />
        )}
      </Content>

      {/* Breakdown Modal */}
      {breakdownModalOpen && breakdownModalData && (
        <BreakdownModal>
          <ModalOverlay onClick={() => setBreakdownModalOpen(false)} />
          <BreakdownModalContent>
            <BreakdownHeader>
              <BreakdownTitle>
                📅 Responses for {breakdownModalData.date}
                <BreakdownSubtitle>
                  {breakdownModalData.count} total response{breakdownModalData.count !== 1 ? 's' : ''}
                </BreakdownSubtitle>
              </BreakdownTitle>
              <CloseButton onClick={() => setBreakdownModalOpen(false)}>
                <CloseIcon />
              </CloseButton>
            </BreakdownHeader>

            <BreakdownTable>
              <thead>
                <tr>
                  <BreakdownTableHeader>Responder ID</BreakdownTableHeader>
                  <BreakdownTableHeader>Project</BreakdownTableHeader>
                  <BreakdownTableHeader>Brand</BreakdownTableHeader>
                  <BreakdownTableHeader>Category</BreakdownTableHeader>
                  <BreakdownTableHeader>Stage</BreakdownTableHeader>
                  <BreakdownTableHeader>Time</BreakdownTableHeader>
                </tr>
              </thead>
              <tbody>
                {breakdownModalData.responders.map((r, idx) => (
                  <tr key={idx}>
                    <BreakdownTableCell>{r.responder_id}</BreakdownTableCell>
                    <BreakdownTableCell>{r.project_code}</BreakdownTableCell>
                    <BreakdownTableCell>{r.brand_name}</BreakdownTableCell>
                    <BreakdownTableCell>{r.product_category}</BreakdownTableCell>
                    <BreakdownTableCell>{r.moment_stage}</BreakdownTableCell>
                    <BreakdownTableCell>{dayjs(r.created_at).format('HH:mm:ss')}</BreakdownTableCell>
                  </tr>
                ))}
              </tbody>
            </BreakdownTable>

            <BreakdownFooter>
              <ActionButton onClick={exportBreakdownCSV}>
                <DownloadIcon /> Export This Day
              </ActionButton>
            </BreakdownFooter>
          </BreakdownModalContent>
        </BreakdownModal>
      )}
    </Container>
  );
};

export default SurveyDashboard;

// Styled Components
const Container = styled.div`
  min-height: 100vh;
  background-color: ${(props) => props.theme.colors.background};
`;

const Header = styled.header`
  background: linear-gradient(135deg, ${(props) => props.theme.colors.primary} 0%, ${(props) => props.theme.colors.primaryDark} 100%);
  padding: ${(props) => props.theme.spacing.xl};
  box-shadow: ${(props) => props.theme.shadows.md};
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing.sm};
  background: rgba(255, 255, 255, 0.1);
  color: ${(props) => props.theme.colors.textPrimary};
  padding: ${(props) => props.theme.spacing.sm} ${(props) => props.theme.spacing.md};
  border-radius: ${(props) => props.theme.borderRadius.md};
  border: 1px solid rgba(255, 255, 255, 0.2);
  font-size: ${(props) => props.theme.fontSizes.sm};
  margin-bottom: ${(props) => props.theme.spacing.md};
  transition: all ${(props) => props.theme.transitions.fast};
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const Title = styled.h1`
  font-size: ${(props) => props.theme.fontSizes['3xl']};
  font-weight: ${(props) => props.theme.fontWeights.bold};
  color: ${(props) => props.theme.colors.textPrimary};
`;

const Content = styled.main`
  max-width: 90%;
  margin: 0 auto;
  padding: ${(props) => props.theme.spacing.xl};
`;

const TabsContainer = styled.div`
  background-color: ${(props) => props.theme.colors.cardBackground};
  border-radius: ${(props) => props.theme.borderRadius.lg};
  margin-bottom: ${(props) => props.theme.spacing.lg};
  box-shadow: ${(props) => props.theme.shadows.sm};
`;

const ControlPanel = styled.div`
  background-color: ${(props) => props.theme.colors.cardBackground};
  border-radius: ${(props) => props.theme.borderRadius.lg};
  padding: ${(props) => props.theme.spacing.lg};
  margin-bottom: ${(props) => props.theme.spacing.lg};
  box-shadow: ${(props) => props.theme.shadows.sm};
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: ${(props) => props.theme.spacing.md};
`;

const FilterSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing.md};
  flex-wrap: wrap;
`;

const FilterLabel = styled.label`
  font-size: ${(props) => props.theme.fontSizes.sm};
  font-weight: ${(props) => props.theme.fontWeights.semibold};
  color: ${(props) => props.theme.colors.textSecondary};
`;

const FilterSelect = styled.select`
  padding: ${(props) => props.theme.spacing.sm} ${(props) => props.theme.spacing.md};
  border-radius: ${(props) => props.theme.borderRadius.md};
  border: 1px solid ${(props) => props.theme.colors.border};
  background-color: white;
  font-size: ${(props) => props.theme.fontSizes.sm};
  cursor: pointer;
  min-width: 150px;

  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
`;

const DateInput = styled.input`
  padding: ${(props) => props.theme.spacing.sm} ${(props) => props.theme.spacing.md};
  border-radius: ${(props) => props.theme.borderRadius.md};
  border: 1px solid ${(props) => props.theme.colors.border};
  background-color: white;
  font-size: ${(props) => props.theme.fontSizes.sm};
  min-width: 150px;
`;

const ExportSection = styled.div`
  display: flex;
  gap: ${(props) => props.theme.spacing.sm};
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing.sm};
  padding: ${(props) => props.theme.spacing.sm} ${(props) => props.theme.spacing.md};
  border-radius: ${(props) => props.theme.borderRadius.md};
  border: none;
  font-size: ${(props) => props.theme.fontSizes.sm};
  font-weight: ${(props) => props.theme.fontWeights.semibold};
  cursor: pointer;
  transition: all ${(props) => props.theme.transitions.fast};
  background-color: ${(props) => props.theme.colors.primary};
  color: white;

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ProjectDateBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: ${(props) => props.theme.colors.textSecondary};
  background: ${(props) => props.theme.colors.surface || props.theme.colors.cardBackground};
  border: 1px solid ${(props) => props.theme.colors.border};
  border-radius: 4px;
  padding: 4px 10px;
  white-space: nowrap;
`;

const StatusDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(props) => props.active ? '#22c55e' : '#f59e0b'};
  margin-right: 2px;
  flex-shrink: 0;
`;

const StatusBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${(props) => props.theme.spacing.sm} ${(props) => props.theme.spacing.md};
  background-color: ${(props) => props.theme.colors.cardBackground};
  border-radius: ${(props) => props.theme.borderRadius.md};
  margin-bottom: ${(props) => props.theme.spacing.md};
  border: 1px solid ${(props) => props.theme.colors.border};
`;

const StatusText = styled.span`
  font-size: ${(props) => props.theme.fontSizes.sm};
  color: ${(props) => props.theme.colors.textSecondary};
  font-weight: ${(props) => props.theme.fontWeights.medium};
`;

const ChartsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing.lg};
`;

const ChartRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${(props) => props.theme.spacing.lg};

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  background-color: ${(props) => props.theme.colors.cardBackground};
  border-radius: ${(props) => props.theme.borderRadius.lg};
  padding: ${(props) => props.theme.spacing.lg};
  box-shadow: ${(props) => props.theme.shadows.md};
`;

const ChartHeader = styled.div`
  margin-bottom: ${(props) => props.theme.spacing.md};
`;

const ChartTitle = styled.h3`
  font-size: ${(props) => props.theme.fontSizes.lg};
  font-weight: ${(props) => props.theme.fontWeights.semibold};
  color: ${(props) => props.theme.colors.textPrimary};
  margin-bottom: ${(props) => props.theme.spacing.xs};
`;

const ChartSubtitle = styled.p`
  font-size: ${(props) => props.theme.fontSizes.sm};
  color: ${(props) => props.theme.colors.textSecondary};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${(props) => props.theme.spacing['3xl']};
  background-color: ${(props) => props.theme.colors.cardBackground};
  border-radius: ${(props) => props.theme.borderRadius.lg};
  margin-top: ${(props) => props.theme.spacing.lg};
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: ${(props) => props.theme.spacing.md};
`;

const EmptyText = styled.p`
  font-size: ${(props) => props.theme.fontSizes.lg};
  color: ${(props) => props.theme.colors.textSecondary};
`;

const ComingSoonCard = styled.div`
  background-color: ${(props) => props.theme.colors.cardBackground};
  border-radius: ${(props) => props.theme.borderRadius.xl};
  padding: ${(props) => props.theme.spacing['3xl']};
  text-align: center;
  border: 1px solid ${(props) => props.theme.colors.border};
`;

const Icon = styled.div`
  font-size: 80px;
  margin-bottom: ${(props) => props.theme.spacing.xl};
`;

const Heading = styled.h2`
  font-size: ${(props) => props.theme.fontSizes['2xl']};
  font-weight: ${(props) => props.theme.fontWeights.semibold};
  color: ${(props) => props.theme.colors.textPrimary};
  margin-bottom: ${(props) => props.theme.spacing.md};
`;

const Description = styled.p`
  font-size: ${(props) => props.theme.fontSizes.lg};
  color: ${(props) => props.theme.colors.textSecondary};
  margin-bottom: ${(props) => props.theme.spacing.xl};
  line-height: 1.6;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  max-width: 500px;
  margin: 0 auto;
`;

const Feature = styled.li`
  font-size: ${(props) => props.theme.fontSizes.base};
  color: ${(props) => props.theme.colors.textSecondary};
  padding: ${(props) => props.theme.spacing.sm} 0;
  text-align: left;
`;

const TooltipContainer = styled.div`
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: ${(props) => props.theme.borderRadius.md};
  padding: ${(props) => props.theme.spacing.sm};
  box-shadow: ${(props) => props.theme.shadows.md};
`;

const TooltipLabel = styled.div`
  font-size: ${(props) => props.theme.fontSizes.sm};
  font-weight: ${(props) => props.theme.fontWeights.semibold};
  color: ${(props) => props.theme.colors.textPrimary};
  margin-bottom: ${(props) => props.theme.spacing.xs};
`;

const TooltipValue = styled.div`
  font-size: ${(props) => props.theme.fontSizes.sm};
  color: ${(props) => props.color || props.theme.colors.textSecondary};
`;

const BreakdownModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
`;

const BreakdownModalContent = styled.div`
  position: relative;
  width: 90%;
  max-width: 1000px;
  max-height: 80vh;
  background-color: white;
  border-radius: ${(props) => props.theme.borderRadius.lg};
  overflow: hidden;
  z-index: 10000;
  display: flex;
  flex-direction: column;
`;

const BreakdownHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: ${(props) => props.theme.spacing.lg};
  background: linear-gradient(135deg, #14b8a6 0%, #0d9488 100%);
  color: white;
`;

const BreakdownTitle = styled.div`
  font-size: ${(props) => props.theme.fontSizes.xl};
  font-weight: ${(props) => props.theme.fontWeights.bold};
`;

const BreakdownSubtitle = styled.div`
  font-size: ${(props) => props.theme.fontSizes.sm};
  opacity: 0.9;
  margin-top: ${(props) => props.theme.spacing.xs};
`;

const BreakdownTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  overflow-y: auto;
  max-height: calc(80vh - 200px);
  display: block;
`;

const BreakdownTableHeader = styled.th`
  background-color: #14b8a6;
  color: white;
  padding: ${(props) => props.theme.spacing.md};
  text-align: left;
  font-weight: ${(props) => props.theme.fontWeights.semibold};
  position: sticky;
  top: 0;
  z-index: 1;
  border-bottom: 2px solid #0d9488;
`;

const BreakdownTableCell = styled.td`
  padding: ${(props) => props.theme.spacing.md};
  border-bottom: 1px solid #e5e7eb;
  color: #374151;
`;

const BreakdownFooter = styled.div`
  padding: ${(props) => props.theme.spacing.md} ${(props) => props.theme.spacing.lg};
  background-color: #f5f5f5;
  border-top: 1px solid #ddd;
  display: flex;
  justify-content: flex-end;
`;

const CloseButton = styled.button`
  background-color: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  transition: background-color ${(props) => props.theme.transitions.fast};

  &:hover {
    background-color: rgba(255, 255, 255, 0.3);
  }
`;

// Table Styled Components
const TableContainer = styled.div`
  background-color: ${(props) => props.theme.colors.cardBackground};
  border-radius: ${(props) => props.theme.borderRadius.lg};
  padding: ${(props) => props.theme.spacing.lg};
  box-shadow: ${(props) => props.theme.shadows.sm};
  overflow-x: auto;
  max-height: 80vh;
  overflow-y: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${(props) => props.theme.fontSizes.sm};
`;

const TableHead = styled.thead`
  background-color: #14b8a6;
  color: white;
  position: sticky;
  top: 0;
  z-index: 10;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid ${(props) => props.theme.colors.border};
  
  &:hover {
    background-color: #f9fafb;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const TableHeader = styled.th`
  padding: ${(props) => props.theme.spacing.md};
  text-align: left;
  font-weight: ${(props) => props.theme.fontWeights.semibold};
  white-space: nowrap;
  background-color: #14b8a6;
  color: white;
`;

const TableCell = styled.td`
  padding: ${(props) => props.theme.spacing.md};
  color: ${(props) => props.theme.colors.textPrimary};
  word-break: break-word;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: ${(props) => props.theme.spacing.xs} ${(props) => props.theme.spacing.sm};
  border-radius: ${(props) => props.theme.borderRadius.md};
  font-size: ${(props) => props.theme.fontSizes.xs};
  font-weight: ${(props) => props.theme.fontWeights.semibold};
  background-color: ${(props) => {
    switch (props.status) {
      case 'completed':
        return '#d1fae5';
      case 'in_progress':
      case 'started':
        return '#fef3c7';
      case 'not_started':
        return '#fee2e2';
      default:
        return '#f3f4f6';
    }
  }};
  color: ${(props) => {
    switch (props.status) {
      case 'completed':
        return '#065f46';
      case 'in_progress':
      case 'started':
        return '#b45309';
      case 'not_started':
        return '#991b1b';
      default:
        return '#374151';
    }
  }};
`;

const JourneyGridContainer = styled.div`
  height: 600px;
  width: 100%;
  background-color: white;
  border-radius: ${(props) => props.theme.borderRadius.lg};
  box-shadow: ${(props) => props.theme.shadows.md};
  margin-top: ${(props) => props.theme.spacing.lg};

  .ag-root {
    font-family: ${(props) => props.theme.fonts.sans};
  }

  .ag-header {
    background-color: #f9fafb;
  }

  .ag-header-cell {
    border-right: none !important;
  }

  .ag-cell {
    border-right: none !important;
    color: #374151;
    font-size: 14px;
  }

  .ag-row {
    border-bottom: 1px solid #e5e7eb;

    &:hover {
      background-color: #f9fafb !important;
    }
  }

  .ag-row-selected {
    background-color: #dbeafe !important;
  }

  .ag-pagination {
    background-color: #f9fafb;
  }
`;
